import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const WEEKS = 8;

const addWeeks = (isoDate, n) => {
  const d = new Date(isoDate + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n * 7);
  return d.toISOString().slice(0, 10);
};

const fmtWeek = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
};

const thisMonday = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
};

const UtilBar = ({ utilisation, over }) => {
  const pct = Math.min(utilisation, 100);
  const colour = over
    ? "#FF007A"
    : utilisation >= 90
    ? "#F59E0B"
    : utilisation >= 60
    ? "#00F5D4"
    : "#374151";
  return (
    <div className="w-full h-1.5 bg-[#1F2937] rounded-full overflow-hidden mt-1">
      <div style={{ width: `${pct}%`, background: colour }} className="h-full rounded-full transition-all" />
    </div>
  );
};

const Capacity = () => {
  const [grid, setGrid] = useState(null);
  const [weekStart, setWeekStart] = useState(thisMonday());
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [allocForm, setAllocForm] = useState({ user_id: "", project_id: "", week_start: thisMonday(), hours: "" });
  const [allocBusy, setAllocBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, p, u] = await Promise.all([
        api.get(`/capacity/weekly-grid?week_start=${weekStart}&weeks=${WEEKS}`),
        api.get("/projects?limit=200"),
        api.get("/team/members"),
      ]);
      setGrid(g.data);
      setProjects(Array.isArray(p.data) ? p.data : p.data?.projects || []);
      setMembers(Array.isArray(u.data) ? u.data : []);
    } catch { toast.error("Failed to load capacity data"); }
    finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const prev = () => setWeekStart((w) => addWeeks(w, -WEEKS));
  const next = () => setWeekStart((w) => addWeeks(w, WEEKS));

  const addAllocation = async () => {
    if (!allocForm.user_id || !allocForm.project_id || !allocForm.hours) return toast.error("Fill all fields");
    setAllocBusy(true);
    try {
      await api.post("/resource-allocations", {
        user_id: allocForm.user_id,
        project_id: allocForm.project_id,
        week_start: allocForm.week_start,
        hours_allocated: Number(allocForm.hours),
      });
      toast.success("Allocation saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setAllocBusy(false); }
  };

  const removeAlloc = async (allocId) => {
    try {
      await api.delete(`/resource-allocations/${allocId}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Remove failed"); }
  };

  const weeks = grid?.weeks || [];

  return (
    <div>
      <PageHeader
        eyebrow="Planning"
        title="Resource Capacity"
        description="Weekly allocation grid — allocated hours vs logged hours vs available capacity."
      />

      <div className="px-5 sm:px-8 lg:px-12 py-6 space-y-6">
        {/* Allocation form */}
        <div className="border border-[#1F2937] bg-[#0D1117] p-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-3">Add allocation</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Person</div>
              <select
                value={allocForm.user_id}
                onChange={(e) => setAllocForm({ ...allocForm, user_id: e.target.value })}
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                data-testid="alloc-user-select"
              >
                <option value="">— person —</option>
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Project</div>
              <select
                value={allocForm.project_id}
                onChange={(e) => setAllocForm({ ...allocForm, project_id: e.target.value })}
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                data-testid="alloc-project-select"
              >
                <option value="">— project —</option>
                {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Week of</div>
              <input
                type="date"
                value={allocForm.week_start}
                onChange={(e) => setAllocForm({ ...allocForm, week_start: e.target.value })}
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                data-testid="alloc-week"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Hours</div>
              <input
                type="number"
                min="1"
                max="40"
                value={allocForm.hours}
                onChange={(e) => setAllocForm({ ...allocForm, hours: e.target.value })}
                placeholder="e.g. 16"
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                data-testid="alloc-hours"
              />
            </div>
            <Button
              onClick={addAllocation}
              disabled={allocBusy}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-9"
              data-testid="alloc-save-btn"
            >
              {allocBusy ? "Saving…" : "Allocate"}
            </Button>
          </div>
        </div>

        {/* Grid */}
        <div className="border border-[#1F2937]">
          {/* Navigation header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1F2937] bg-[#111827]">
            <Button variant="ghost" onClick={prev} className="rounded-none h-7 w-7 p-0 text-[#6B7280] hover:text-white">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-xs font-mono text-[#9CA3AF]">
              {fmtWeek(weeks[0] || weekStart)} → {fmtWeek(weeks[weeks.length - 1] || weekStart)}
            </div>
            <Button variant="ghost" onClick={next} className="rounded-none h-7 w-7 p-0 text-[#6B7280] hover:text-white">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <div className="py-16 text-center text-sm text-[#6B7280]">Loading…</div>
          ) : !grid?.rows?.length ? (
            <div className="py-16 text-center text-sm text-[#6B7280]" data-testid="capacity-empty">
              No team members found. Invite your team first.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-[#1F2937]">
                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] w-48 bg-[#0D1117]">
                      Team member
                    </th>
                    {weeks.map((w) => (
                      <th key={w} className="text-center px-2 py-3 text-[10px] uppercase tracking-[0.1em] text-[#6B7280] bg-[#0D1117]">
                        {fmtWeek(w)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grid.rows.map((row) => (
                    <tr key={row.user_id} className="border-b border-[#1F2937] hover:bg-[#030712]">
                      <td className="px-4 py-3 bg-[#0D1117]">
                        <div className="font-medium text-white truncate">{row.name}</div>
                        <div className="text-[10px] text-[#6B7280]">{row.role}</div>
                        <div className="text-[10px] text-[#4B5563]">{row.weekly_capacity_hours}h/wk</div>
                      </td>
                      {row.cells.map((cell) => {
                        const isEmpty = cell.allocated === 0 && cell.logged === 0;
                        return (
                          <td key={cell.week_start} className="px-2 py-2 text-center align-top bg-[#0D1117]">
                            {isEmpty ? (
                              <div className="text-[#374151] text-xs">—</div>
                            ) : (
                              <div className="space-y-0.5">
                                <div
                                  className={`text-xs font-mono font-bold ${
                                    cell.over_capacity ? "text-[#FF007A]" : cell.utilisation >= 90 ? "text-[#F59E0B]" : "text-[#00F5D4]"
                                  }`}
                                  title={`Allocated: ${cell.allocated}h / Available: ${cell.available}h`}
                                >
                                  {cell.allocated}h
                                  {cell.over_capacity && <AlertTriangle className="w-3 h-3 inline ml-0.5" />}
                                </div>
                                <UtilBar utilisation={cell.utilisation} over={cell.over_capacity} />
                                {cell.logged > 0 && (
                                  <div className="text-[10px] text-[#6B7280]">{cell.logged}h logged</div>
                                )}
                                {cell.projects.length > 0 && (
                                  <div className="text-[9px] text-[#4B5563] truncate max-w-[80px] mx-auto">
                                    {cell.projects.map((p) => p.project_name || p.project_id).join(", ")}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 text-[11px] text-[#6B7280]">
          <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-[#374151] inline-block" /> Under 60%</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-[#00F5D4] inline-block" /> 60–89%</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-[#F59E0B] inline-block" /> 90–99%</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-[#FF007A] inline-block" /> Over capacity</span>
          <span className="ml-auto">Numbers show allocated hours · Logged hours shown in grey below</span>
        </div>
      </div>
    </div>
  );
};

export default Capacity;
